import { useMemo } from 'react';
import { useGetMenuItemStructureQuery } from '../menu';
import type { MenuItem } from '../menu';

type UseResolveMenuButtonResult = {
  menuButtonFromParentStructure: MenuItem | null;
  effectiveMenuItemId: number | undefined;
  effectiveNodeKey: string | undefined;
};

// Finds a menu item whose link shares all path segments with targetPath.
// Uses segment-level prefix matching to avoid false positives like
// /finances/invoices-other matching /finances/invoices.
export const findMenuItemByPathPrefix = (
  items: MenuItem[],
  targetPath: string
): MenuItem | null => {
  const targetSegments = targetPath.toLowerCase().split('/').filter(Boolean);

  const matches = (link: string): boolean => {
    const linkSegments = link.toLowerCase().split('/').filter(Boolean);
    if (linkSegments.length < targetSegments.length) return false;
    return targetSegments.every((seg, i) => linkSegments[i] === seg);
  };

  for (const item of items) {
    if (item.link && matches(item.link)) return item;
    if (item.childs?.length) {
      const found = findMenuItemByPathPrefix(item.childs, targetPath);
      if (found) return found;
    }
  }
  return null;
};

export const useResolveMenuButton = ({
  menuItem,
  menuItems,
  basePath,
}: {
  menuItem: MenuItem | null | undefined;
  menuItems: MenuItem[];
  basePath: string;
}): UseResolveMenuButtonResult => {
  const parentMenuItemForBase = useMemo(() => {
    if (menuItem) return null;
    const parts = basePath.split('/').filter(Boolean);
    if (parts.length <= 1) return null;
    parts.pop();
    const parentPath = '/' + parts.join('/');
    return findMenuItemByPathPrefix(menuItems, parentPath);
  }, [menuItem, menuItems, basePath]);

  const parentMenuItemId = useMemo(() => parentMenuItemForBase?.id ?? 0, [parentMenuItemForBase]);

  const { data: parentStructure } = useGetMenuItemStructureQuery(
    { menuItemId: parentMenuItemId },
    { skip: !!menuItem || !parentMenuItemId }
  );

  const menuButtonFromParentStructure = useMemo<MenuItem | null>(() => {
    if (menuItem || !parentStructure?.buttons) return null;
    // Normalize both sides: strip trailing slash and lowercase
    const normalizedBase = basePath.replace(/\/$/, '').toLowerCase();
    return (
      parentStructure.buttons.find(
        (b) => (b.link ?? '').replace(/\/$/, '').toLowerCase() === normalizedBase
      ) ?? null
    );
  }, [menuItem, parentStructure, basePath]);

  return {
    menuButtonFromParentStructure,
    effectiveMenuItemId: menuButtonFromParentStructure?.id ?? undefined,
    effectiveNodeKey: menuButtonFromParentStructure?.nodeKey ?? undefined,
  };
};
